import { getTimeDifference } from '@libs/utils';

import {
  calcBuyContractReturnValue,
  calcSellContractReturnValue,
  PositionItemWithPrices,
} from '../utils/calculator';
import { DAY_PER_MILLISECONDS, POSITIONS_SIDE } from '../constants';
import { useGetServerClock } from '@core/order';

export type TUseOptionPositionDetailMapperReturn = ReturnType<
  typeof useOptionPositionDetailMapper
>;

export default function useOptionPositionDetailMapper(
  option: PositionItemWithPrices
) {
  const {
    cashSettlementDate,
    physicalSettlementDate,
    underlyingAsseClosingPrice,
    contractType,
    strikePrice,
    cashSettlementFee,
    allowCashSettlementRequest,
    allowPhysicalSettlementRequest,
    positionSide,
  } = option;

  const { time } = useGetServerClock({
    immediate: false,
  });

  const contractReturnValue =
    contractType === POSITIONS_SIDE.buy
      ? calcBuyContractReturnValue({
          strikePrice,
          cashSettlementFee,
          underlyingAsseClosingPrice,
        })
      : calcSellContractReturnValue({
          strikePrice,
          cashSettlementFee,
          underlyingAsseClosingPrice,
        });

  const settlementDateDetail = (date: string) => {
    const dateDetail: {
      hasFinished: boolean;
      isStart: boolean;
    } = { hasFinished: false, isStart: false };

    const { differenceInDays } = getTimeDifference({
      startTime: new Date(time.value).getTime(),
      endTime: new Date(date).getTime() + DAY_PER_MILLISECONDS,
    });
    if (differenceInDays === 0) {
      dateDetail.isStart = true;
    } else if (differenceInDays < 0) {
      dateDetail.hasFinished = true;
    }

    return dateDetail;
  };

  const {
    hasFinished: hasFinishedPhysicalSettlement,
    isStart: isInPhysicalSettlementDay,
  } = settlementDateDetail(physicalSettlementDate);
  const {
    hasFinished: hasFinishedCashSettlement,
    isStart: isInCashSettlementDay,
  } = settlementDateDetail(cashSettlementDate);

  const isCashSettlementRequestAllowed =
    isInCashSettlementDay && allowCashSettlementRequest;
  const isPhysicalSettlementRequestAllowed =
    isInPhysicalSettlementDay && allowPhysicalSettlementRequest;

  const isCashSettlementForbidden =
    isInCashSettlementDay && contractReturnValue <= 0;

  const isSellContractForbidden =
    positionSide === POSITIONS_SIDE.sell && isInPhysicalSettlementDay;

  return {
    ...option,
    hasFinishedPhysicalSettlement,
    hasFinishedCashSettlement,

    isCashSettlementRequestAllowed,
    isPhysicalSettlementRequestAllowed,
    isCashSettlementForbidden,
    isSellContractForbidden,

    isInPhysicalSettlementDay,
    isInCashSettlementDay,
    contractReturnValue,
  };
}
